import React, { useState, useCallback, useEffect, useMemo } from "react";
import { getApiBaseUrl } from "../../../../../helpers/apiUrl";
import ReviewStep from "../shared/ReviewStep";
import ContentViewer from "../shared/ContentViewer";
import TopicPicker from "../shared/TopicPicker";
import QuizPlayer from "../shared/QuizPlayer";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Skeleton,
} from "@mui/material";
import {
  ArrowBack,
  Quiz as QuizIcon,
  Add,
  School,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGenerateLearnQuizMutation, useGetLearnHistoryQuery } from "../../../../../app/learnApiSlice";
import { useGetQuizResultQuery, useGetMyStudentSubjectsQuery } from "../../../../../app/quizApiSlice";

// ─── Keyframes ────────────────────────────────────────────────────────────────
const popIn = keyframes`
  from { opacity: 0; transform: scale(0.88) translateY(10px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;
const confettiFall = keyframes`
  0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(120px) rotate(540deg); opacity: 0; }
`;
const scorePop = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1);   opacity: 1; }
`;

// ─── Step IDs ────────────────────────────────────────────────────────────────
const STEP_SETUP = "setup";
const STEP_LOADING = "loading";
const STEP_CONTENT = "content";
const STEP_QUIZ = "quiz";
const STEP_RESULT = "result";

// ─── Fallback subjects (used when API returns empty) ──────────────────────────
const FALLBACK_SUBJECTS = [
  "Mathematics", "Physics", "Chemistry", "Biology",
  "English Language", "History", "Geography", "Computer Science",
  "Economics", "Further Maths",
];

// ─── Subject card color palettes (matches PracticeQuizFlow) ──────────────────
const SUBJECT_PALETTES = [
  { bg: "#1e3a5f", accent: "#3b82f6", glow: "rgba(59,130,246,0.35)" },
  { bg: "#1a3828", accent: "#22c55e", glow: "rgba(34,197,94,0.35)" },
  { bg: "#3b1a4a", accent: "#a855f7", glow: "rgba(168,85,247,0.35)" },
  { bg: "#3d1f06", accent: "#f97316", glow: "rgba(249,115,22,0.35)" },
  { bg: "#1e2a4a", accent: "#6366f1", glow: "rgba(99,102,241,0.35)" },
  { bg: "#1a3030", accent: "#14b8a6", glow: "rgba(20,184,166,0.35)" },
  { bg: "#3d1a1a", accent: "#ef4444", glow: "rgba(239,68,68,0.35)" },
  { bg: "#2a2a0e", accent: "#eab308", glow: "rgba(234,179,8,0.35)" },
];

// ─── Subject Card (matches PracticeQuizFlow style) ───────────────────────────
const SubjectCard = ({ name, palette, onClick, delay = "0s" }) => {
  const [hovered, setHovered] = useState(false);
  const initials = name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        background: hovered ? `${palette.bg}ee` : palette.bg,
        borderRadius: "20px",
        p: "20px 18px",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.2s ease",
        transform: hovered ? "translateY(-4px) scale(1.02)" : "scale(1)",
        boxShadow: hovered
          ? `0 12px 32px ${palette.glow}, 0 2px 8px rgba(0,0,0,0.4)`
          : "0 4px 16px rgba(0,0,0,0.35)",
        border: `1.5px solid ${hovered ? palette.accent : "rgba(255,255,255,0.08)"}`,
        animation: `${popIn} 0.4s ${delay} ease both`,
        userSelect: "none",
      }}
    >
      <Box sx={{ position: "absolute", top: -16, right: -16, width: 80, height: 80, borderRadius: "50%", bgcolor: `${palette.accent}18`, pointerEvents: "none" }} />
      <Box sx={{
        width: 44, height: 44, borderRadius: "14px",
        bgcolor: `${palette.accent}22`, border: `1.5px solid ${palette.accent}55`,
        display: "flex", alignItems: "center", justifyContent: "center",
        mb: 1.5,
      }}>
        <Typography sx={{ fontWeight: 900, fontSize: "1rem", color: palette.accent }}>{initials}</Typography>
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#fff", lineHeight: 1.3, mb: 0.3 }}>
        {name}
      </Typography>
      <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", fontWeight: 500 }}>Tap to start</Typography>
    </Box>
  );
};

// ─── Result Step ──────────────────────────────────────────────────────────────
const ResultStep = ({ quizId, onReview, onNewLesson }) => {
  const { data, isLoading } = useGetQuizResultQuery({ quiz_id: quizId });

  const stats = React.useMemo(() => {
    if (!data?.answers) return { correct: 0, wrong: 0, total: 0, pct: 0 };
    let correct = 0;
    data.answers.forEach((a) => {
      if (a.question?.options?.find((o) => o.id === a.option_id && o.is_answer)) correct++;
    });
    const total = data.answers.length;
    return { correct, wrong: total - correct, total, pct: total ? Math.round((correct / total) * 100) : 0 };
  }, [data]);

  if (isLoading) return <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress sx={{ color: "#7C3AED" }} /></Box>;

  const stars = stats.pct >= 80 ? 3 : stats.pct >= 55 ? 2 : 1;
  const emoji = stats.pct >= 80 ? "🏆" : stats.pct >= 55 ? "🎯" : "💪";

  const confettiDots = Array.from({ length: 16 }, (_, i) => ({
    left: `${((i * 6 + 5) % 90) + 5}%`,
    color: ["#FF6B9D", "#FFD700", "#4FC3F7", "#A5D6A7", "#FF8A65", "#CE93D8"][i % 6],
    delay: `${(i * 0.07).toFixed(2)}s`,
    size: 5 + (i % 4) * 2,
  }));

  return (
    <Box sx={{ background: "linear-gradient(160deg, #1a0533 0%, #2d1b69 50%, #1a0533 100%)", p: { xs: 3, sm: 4 }, minHeight: "100vh", textAlign: "center", position: "relative", overflow: "hidden" }}>
      {confettiDots.map((d, i) => (
        <Box key={i} sx={{ position: "absolute", top: "-10px", left: d.left, width: d.size, height: d.size, borderRadius: "50%", bgcolor: d.color, animation: `${confettiFall} 1.2s ${d.delay} ease-out forwards` }} />
      ))}

      <Typography sx={{ fontSize: "4rem", lineHeight: 1, mb: 1 }}>{emoji}</Typography>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2 }}>
        {[1, 2, 3].map((s) => (
          <Typography key={s} sx={{ fontSize: "2rem", filter: s <= stars ? "drop-shadow(0 0 6px #FFD700)" : "grayscale(1) opacity(0.3)", animation: s <= stars ? `${scorePop} 0.5s ${(s - 1) * 0.15}s ease both` : "none" }}>⭐</Typography>
        ))}
      </Box>
      <Box sx={{ display: "inline-block", px: 4, py: 1.5, mb: 2, borderRadius: "50px", background: "rgba(255,255,255,0.12)", border: "2px solid rgba(255,255,255,0.25)", animation: `${scorePop} 0.6s 0.1s ease both` }}>
        <Typography sx={{ fontWeight: 900, fontSize: "2.8rem", color: "#fff", lineHeight: 1 }}>
          {stats.pct}<Typography component="span" sx={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.7)" }}>%</Typography>
        </Typography>
      </Box>
      <Typography sx={{ color: "rgba(255,255,255,0.7)", mb: 3 }}>{stats.correct} correct out of {stats.total} questions</Typography>
      <Grid container spacing={2} justifyContent="center" sx={{ mb: 3.5, maxWidth: 320, mx: "auto" }}>
        <Grid item xs={6}>
          <Box sx={{ p: 2, borderRadius: "14px", background: "rgba(34,197,94,0.18)", border: "1.5px solid rgba(34,197,94,0.4)" }}>
            <Typography sx={{ fontWeight: 900, fontSize: "1.6rem", color: "#4ade80" }}>{stats.correct}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>CORRECT</Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ p: 2, borderRadius: "14px", background: "rgba(239,68,68,0.18)", border: "1.5px solid rgba(239,68,68,0.4)" }}>
            <Typography sx={{ fontWeight: 900, fontSize: "1.6rem", color: "#f87171" }}>{stats.wrong}</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", fontWeight: 600 }}>WRONG</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Action buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "center" }}>
        {stats.wrong > 0 && (
          <Button
            onClick={onReview}
            variant="contained"
            sx={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)", color: "#fff", fontWeight: 800, borderRadius: "14px", px: 4, py: 1.4, boxShadow: "0 6px 20px rgba(79,70,229,0.4)", width: "100%", maxWidth: 320, "&:hover": { background: "linear-gradient(135deg, #4338ca, #6d28d9)" } }}
          >
            Review Wrong Answers
          </Button>
        )}
        <Button
          onClick={onNewLesson}
          variant="outlined"
          sx={{ color: "rgba(255,255,255,0.85)", borderColor: "rgba(255,255,255,0.3)", fontWeight: 700, borderRadius: "14px", px: 4, py: 1.3, width: "100%", maxWidth: 320, "&:hover": { borderColor: "rgba(255,255,255,0.7)", bgcolor: "rgba(255,255,255,0.08)" } }}
        >
          New Lesson
        </Button>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const LearnFlow = () => {
  const [step, setStep] = useState(STEP_SETUP);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [topicPickerSubject, setTopicPickerSubject] = useState(null);
  const [showCustomSubject, setShowCustomSubject] = useState(false);
  const [customSubjectInput, setCustomSubjectInput] = useState("");
  const [loadingStatus, setLoadingStatus] = useState("Building your lesson…");
  const [error, setError] = useState("");
  const [session, setSession] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [completedQuizId, setCompletedQuizId] = useState(null);
  const [showReview, setShowReview] = useState(false);

  const authData = useSelector((state) => state.auth);
  const accessToken = authData?.access_token;
  const baseUrl = getApiBaseUrl();
  const navigate = useNavigate();

  const { data: subjectData, isLoading: loadingSubjects } = useGetMyStudentSubjectsQuery();
  const subjects = subjectData?.subjects?.length
    ? subjectData.subjects.map((s) => s.name)
    : FALLBACK_SUBJECTS;

  const [generateLearnQuiz, { isLoading: isGeneratingQuiz }] = useGenerateLearnQuizMutation();

  // Fetch recent learn history for "Continue Last Lesson" option
  const { data: historyData } = useGetLearnHistoryQuery({ page: 1, per_page: 10 });
  const lastTopicsForSubject = useMemo(() => {
    if (!topicPickerSubject || !historyData?.sessions) return [];
    const match = historyData.sessions.find(
      (s) => s.subject?.toLowerCase() === topicPickerSubject.toLowerCase() && s.topics?.length
    );
    return match?.topics || [];
  }, [topicPickerSubject, historyData]);

  // ── Start lesson: call /learn/start with subject (and optional topic) ──────
  const startLesson = useCallback(async (subject, topic, { continuation = false } = {}) => {
    let message;
    if (continuation && topic?.trim()) {
      message = `This is a FOLLOW-UP session continuing from my last lesson on ${subject}. My previous topics were: ${topic.trim()}. Generate a deeper, more advanced lesson that builds on what I already learned — skip introductions I've already seen, go deeper into sub-topics, cover harder examples and edge cases, and include educational videos.`;
    } else if (topic?.trim()) {
      message = `Generate a complete lesson on ${subject}, focusing specifically on the topic: ${topic.trim()}. Use exactly that one topic and include educational videos.`;
    } else {
      message = `Generate a complete lesson on ${subject}. Select exactly 1 relevant topic for my level and include educational videos.`;
    }

    setSelectedSubject(subject);
    setLoadingStatus("Generating your lesson…");
    setStep(STEP_LOADING);
    setError("");
    setSession(null);
    setSessionId(null);

    try {
      const response = await fetch(`${baseUrl}/learn/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ message, subject, history: [] }),
      });
      if (!response.ok) throw new Error(`Request failed (${response.status})`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let lineBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        lineBuffer = lines[lines.length - 1];

        for (let i = 0; i < lines.length - 1; i++) {
          const line = lines[i].trim();
          if (!line.startsWith("data: ")) continue;
          let parsed;
          try { parsed = JSON.parse(line.substring(6).trim()); } catch { continue; }

          if (parsed.type === "tool_call") {
            if (parsed.name === "generate_learn_content") setLoadingStatus("✨ Creating your lesson content…");
            else if (parsed.name === "search_youtube_videos") setLoadingStatus("🎬 Finding related videos…");
            else setLoadingStatus("Thinking…");
          } else if (parsed.type === "content_ready") {
            // Normalize SSE payload → flat session object ContentViewer expects
            const c = parsed.content || {};
            setSession({
              subject: c.subject || subject,
              level: c.level || "",
              topics: c.topics || [],
              content: c.content_blocks || [],
            });
            if (parsed.session_id) setSessionId(parsed.session_id);
            setStep(STEP_CONTENT);
          } else if (parsed.type === "error") {
            setError(parsed.content || "An error occurred");
            setStep(STEP_SETUP);
          }
        }
      }

      // If stream ended without content_ready, show error and go back to setup
      setStep((prev) => {
        if (prev === STEP_LOADING) {
          setError("Couldn't generate the lesson. Please try again.");
          return STEP_SETUP;
        }
        return prev;
      });
    } catch (err) {
      setError(err.message || "Failed to reach AI");
      setStep(STEP_SETUP);
    }
  }, [baseUrl, accessToken]);

  const handleTakeQuiz = async () => {
    if (!sessionId) return;
    try {
      const result = await generateLearnQuiz(sessionId).unwrap();
      setQuiz(result.quiz);
      setStep(STEP_QUIZ);
    } catch (err) {
      setError("Failed to generate quiz. Please try again.");
    }
  };

  const handleQuizDone = (qid) => { setCompletedQuizId(qid); setStep(STEP_RESULT); setShowReview(false); };
  const handleNewLesson = () => {
    setStep(STEP_SETUP);
    setSession(null);
    setSessionId(null);
    setQuiz(null);
    setTopicPickerSubject(null);
    setShowCustomSubject(false);
    setCustomSubjectInput("");
    setShowReview(false);
  };

  // ── Non-setup steps ────────────────────────────────────────────────────────
  if (step === STEP_CONTENT && session) {
    return (
      <ContentViewer
        session={session}
        sessionId={sessionId}
        onContinue={handleTakeQuiz}
        continueLabel={isGeneratingQuiz ? "Generating…" : "Take Quiz ✨"}
        continueIcon={<QuizIcon />}
        isLoadingContinue={isGeneratingQuiz}
        onGoBack={handleNewLesson}
        accentColor="#7C3AED"
        headerGradient="linear-gradient(135deg, #4A148C 0%, #7C3AED 60%, #A855F7 100%)"
        modeLabel="Learn Mode"
        ctaHeadline="Ready to test yourself?"
        ctaSubtext="Take a short quiz on what you just learned"
      />
    );
  }

  if (step === STEP_QUIZ && quiz) {
    return (
      <Box sx={{ background: "linear-gradient(160deg, #071524 0%, #0d2137 50%, #071524 100%)", minHeight: "100vh", p: { xs: 2.5, sm: 3 } }}>
        <QuizPlayer quiz={quiz} onDone={handleQuizDone} onBack={() => setStep(STEP_CONTENT)} accentColor="#7C3AED" mode="LEARN" />
      </Box>
    );
  }

  if (step === STEP_RESULT && completedQuizId) {
    if (showReview) {
      return (
        <ReviewStep
          quizId={completedQuizId}
          onBack={() => setShowReview(false)}
          onDone={handleNewLesson}
        />
      );
    }
    return (
      <ResultStep
        quizId={completedQuizId}
        onReview={() => setShowReview(true)}
        onNewLesson={handleNewLesson}
      />
    );
  }

  // ── Loading step ──────────────────────────────────────────────────────────
  if (step === STEP_LOADING) {
    return (
      <Box sx={{ background: "linear-gradient(160deg, #1a0533 0%, #2d1b69 50%, #120226 100%)", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: 3, textAlign: "center" }}>
        {/* Pulsing orb */}
        <Box sx={{
          width: 90, height: 90, borderRadius: "50%",
          background: "linear-gradient(135deg, #4A148C, #7C3AED)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 0 48px rgba(124,58,237,0.5)",
          mb: 3,
          animation: `${popIn} 0.4s ease`,
        }}>
          <School sx={{ fontSize: 40, color: "#fff" }} />
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: "1.25rem", color: "#fff", mb: 1, animation: `${popIn} 0.4s 0.1s ease both` }}>
          Building your lesson on
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: "1.5rem", color: "#c084fc", mb: 2, animation: `${popIn} 0.4s 0.15s ease both` }}>
          {selectedSubject}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, animation: `${popIn} 0.4s 0.2s ease both` }}>
          <CircularProgress size={18} sx={{ color: "#a78bfa" }} />
          <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.65)", fontWeight: 600 }}>
            {loadingStatus}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.3)", maxWidth: 280 }}>
          Your lesson is being personalised based on your level and progress
        </Typography>
      </Box>
    );
  }

  // ── Setup step (subject card picker) ──────────────────────────────────────
  return (
    <Box sx={{ background: "linear-gradient(160deg, #071524 0%, #0d2137 50%, #071524 100%)", minHeight: "100vh", px: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 9 } }}>

      {/* Top bar */}
      <Box sx={{ pt: 2.5, pb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton
          onClick={() => navigate("/lms/student/quiz/play")}
          size="small"
          sx={{ color: "rgba(255,255,255,0.8)", bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", "&:hover": { bgcolor: "rgba(255,255,255,0.14)" } }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ width: 36, height: 36, borderRadius: "12px", background: "linear-gradient(135deg, #4A148C, #7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 14px rgba(124,58,237,0.4)" }}>
          <School sx={{ fontSize: 19, color: "#fff" }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: "0.58rem", color: "rgba(255,255,255,0.4)", letterSpacing: 3, textTransform: "uppercase" }}>Learn Mode</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: "0.96rem", color: "#fff", lineHeight: 1.2 }}>AI Learn Mode</Typography>
        </Box>
      </Box>
      <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.07)", mb: 3 }} />

      {/* Heading */}
      <Box sx={{ textAlign: "center", mb: 3, animation: `${popIn} 0.35s ease` }}>
        <Typography sx={{ fontWeight: 900, fontSize: "1.3rem", color: "#fff", mb: 0.5 }}>
          What would you like to learn? 📚
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.4)" }}>
          Choose a subject — we'll build a personalised lesson just for you
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }} onClose={() => setError("")}>{error}</Alert>}

      {/* Subject grid */}
      {loadingSubjects ? (
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={6} sm={4} key={i}>
              <Skeleton variant="rounded" height={110} sx={{ borderRadius: "20px", bgcolor: "rgba(255,255,255,0.07)" }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {subjects.map((subjectName, idx) => (
            <Grid item xs={6} sm={4} key={subjectName}>
              <SubjectCard
                name={subjectName}
                palette={SUBJECT_PALETTES[idx % SUBJECT_PALETTES.length]}
                onClick={() => setTopicPickerSubject(subjectName)}
                delay={`${idx * 0.05}s`}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* "Other subject" custom input */}
      {!showCustomSubject ? (
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Button
            onClick={() => setShowCustomSubject(true)}
            startIcon={<Add />}
            sx={{ color: "rgba(255,255,255,0.45)", fontWeight: 700, fontSize: "0.82rem", "&:hover": { color: "rgba(255,255,255,0.75)", bgcolor: "rgba(255,255,255,0.06)" }, borderRadius: "10px" }}
          >
            Other subject
          </Button>
        </Box>
      ) : (
        <Box sx={{ maxWidth: 400, mx: "auto", mb: 4, animation: `${popIn} 0.3s ease` }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(124,58,237,0.4)", borderRadius: "14px", px: 2, py: 0.5, "&:focus-within": { borderColor: "rgba(124,58,237,0.7)" } }}>
            <TextField
              fullWidth autoFocus variant="standard"
              value={customSubjectInput}
              onChange={(e) => setCustomSubjectInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && customSubjectInput.trim()) setTopicPickerSubject(customSubjectInput.trim()); }}
              placeholder="Type any subject…"
              InputProps={{ disableUnderline: true }}
              sx={{ "& .MuiInputBase-input": { color: "#fff", fontSize: "0.95rem", py: 1, "&::placeholder": { color: "rgba(255,255,255,0.25)", opacity: 1 } } }}
            />
            <Button
              onClick={() => { if (customSubjectInput.trim()) setTopicPickerSubject(customSubjectInput.trim()); }}
              disabled={!customSubjectInput.trim()}
              variant="contained"
              sx={{ background: customSubjectInput.trim() ? "linear-gradient(90deg, #4A148C, #7C3AED)" : "rgba(255,255,255,0.07)", color: customSubjectInput.trim() ? "#fff" : "rgba(255,255,255,0.3)", fontWeight: 800, borderRadius: "10px", px: 2, flexShrink: 0, "&.Mui-disabled": { background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.3)" }, boxShadow: "none" }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Topic Picker Dialog */}
      <TopicPicker
        open={Boolean(topicPickerSubject)}
        onClose={() => setTopicPickerSubject(null)}
        subject={topicPickerSubject || ""}
        accentColor="#7C3AED"
        lastSessionTopics={lastTopicsForSubject}
        onStart={(topic, opts) => {
          const subj = topicPickerSubject;
          setTopicPickerSubject(null);
          startLesson(subj, topic, opts);
        }}
      />
    </Box>
  );
};

export default LearnFlow;
